import React from 'react';
import {
  MobileHeader, SegmentedControl, Button, Badge, EmptyState, SkeletonCard,
} from '@parsina/framework';
import { UserX, Stethoscope, MessagesSquare } from 'lucide-react';

import { CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { PracticeBanner, PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import { ageLabel, patientName } from '../../lib/caseForm.js';
import { CaseFormDialog, DeletePatientDialog } from '../Patients/PatientDialogs.jsx';
import StartVisitDialog from './StartVisitDialog.jsx';
import RecordBody, { RecordMenu } from './RecordBody.jsx';
import {
  RECORD_TABS, TAB_LABEL_KEYS, headerChips, startVisitLabelKey,
} from './PatientRecord.shared.js';

// The patient record on a phone.
//
// A drilled-into surface, so the header is in CONTENT mode and carries the way
// back — the bottom bar is still there (core.md §7.0 hides it only on the visit
// and the chat page), but a student who opened a patient from the list expects
// the back arrow to return them to the list, not to the last destination they
// tapped.
//
// The two primary actions sit under the tab strip rather than in the header:
// «شروع ویزیت» is a 44px target and it must not compete with the back arrow for
// the same corner.

const SCROLL = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px 14px calc(24px + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

export default function PatientRecordMobile({ screen, tab, onTab, caseId }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const { caseRow } = screen;

  if (screen.loading) {
    return (
      <div style={SCROLL} data-testid="record-loading">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (screen.error) {
    return (
      <div style={SCROLL}>
        <ErrorState error={screen.error} onRetry={screen.reload} data-testid="record-error" />
      </div>
    );
  }

  if (screen.notFound) {
    return (
      <div style={SCROLL}>
        <EmptyState
          icon={<UserX size={44} strokeWidth={1.5} aria-hidden />}
          title={t('record.notFound.title')}
          description={t('record.notFound.body')}
          action={
            <Button variant="outline" onClick={screen.back} data-testid="record-not-found-back">
              {t('record.notFound.action')}
            </Button>
          }
          data-testid="record-not-found"
        />
      </div>
    );
  }

  const chips = headerChips(caseRow, { t, formatNumber, formatDate, ageLabel });

  return (
    <>
      <MobileHeader
        onBack={screen.back}
        backLabel={t('common.actions.back')}
        title={patientName(caseRow, t)}
        subtitle={chips.map((chip) => chip.label).join(' · ')}
        action={<RecordMenu screen={screen} onGradient />}
        data-testid="record-mobile-header"
      />

      <div style={SCROLL} data-testid="record-mobile">
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <PracticeBadge />
          {screen.archived && (
            <Badge variant="outline" data-testid="record-archived">
              {t('record.header.archived')}
            </Badge>
          )}
        </div>

        <PracticeBanner />

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={screen.startVisit}
            disabled={screen.archived}
            className="flex-1 min-h-11"
            data-testid="record-start-visit"
          >
            <Stethoscope size={15} aria-hidden />
            {t(startVisitLabelKey(caseRow))}
          </Button>
          <Button
            variant="outline"
            onClick={() => screen.openChat()}
            className="min-h-11"
            data-testid="record-chat"
          >
            <MessagesSquare size={15} aria-hidden />
          </Button>
        </div>

        {screen.archived && (
          <p style={{ margin: 0, fontSize: 12, color: CT.mute }}>
            {t('record.header.archivedHint')}
          </p>
        )}

        {/* The strip scrolls horizontally rather than wrapping: four tabs at a
            44px height wrap to two rows on a 320px viewport, and a tab bar that
            changes height as the label language changes is worse than one that
            slides. */}
        <div style={{ overflowX: 'auto', margin: '0 -14px', padding: '0 14px' }}>
          <SegmentedControl
            options={RECORD_TABS.map((id) => ({ value: id, label: t(TAB_LABEL_KEYS[id]) }))}
            value={tab}
            onChange={onTab}
            size="lg"
            ariaLabel={t('record.tabs.overview')}
            data-testid="record-tabs"
          />
        </div>

        <RecordBody tab={tab} screen={screen} caseId={caseId} mobile />
      </div>

      <StartVisitDialog
        open={screen.startOpen}
        onClose={screen.closeStart}
        reason={screen.reason}
        onReason={screen.setReason}
        onSubmit={screen.submitStart}
        saving={screen.starting}
      />
      <CaseFormDialog
        open={screen.editOpen}
        onClose={screen.closeEdit}
        values={screen.form}
        onChange={screen.setForm}
        onSubmit={screen.submitEdit}
        saving={screen.savingEdit}
        showTitleError={screen.showTitleError}
        mode="edit"
      />
      <DeletePatientDialog
        row={screen.deleteOpen ? caseRow : null}
        onCancel={screen.closeDelete}
        onConfirm={screen.confirmDelete}
        loading={screen.deleting}
      />
    </>
  );
}
